import { useMemo, useState } from "react";
import { FiCheck, FiGitBranch, FiSettings, FiPlay, FiSearch } from "react-icons/fi";
import { useTranslation } from "react-i18next";

// Badge `kind` is the wire-stable identifier ("optional" / "required") so the
// JSX can branch on it for styling; the rendered text is taken from t(). Same
// idea for the completed-state copy.
const STEP_DEFS = [
  {
    number: 1,
    titleKey: "credentialScanning.step1Title",
    badgeKind: "optional",
    badgeColor: "#6b7280",
    descriptionKey: "credentialScanning.step1Description",
    ctaKey: "credentialScanning.step1Cta",
    ctaPath: "git-repos/new",
    skipLabelKey: "credentialScanning.step1SkipLabel",
    icon: <FiGitBranch size={18} />,
    completedKey: "gitRepo",
    completedTextKey: "credentialScanning.step1CompletedText",
  },
  {
    number: 2,
    titleKey: "credentialScanning.step2Title",
    badgeKind: "required",
    badgeColor: "#dc2626",
    descriptionKey: "credentialScanning.step2Description",
    ctaKey: "credentialScanning.step2Cta",
    ctaPath: "configs/new",
    icon: <FiSettings size={18} />,
    completedKey: "config",
    completedTextKey: "credentialScanning.step2CompletedText",
  },
  {
    number: 3,
    titleKey: "credentialScanning.step3Title",
    badgeKind: "required",
    badgeColor: "#dc2626",
    descriptionKey: "credentialScanning.step3Description",
    ctaKey: "credentialScanning.step3Cta",
    ctaPath: "configs",
    icon: <FiPlay size={18} />,
    completedKey: "scan",
    completedTextKey: "credentialScanning.step3CompletedText",
    requiresPrevious: "config",
  },
  {
    number: 4,
    titleKey: "credentialScanning.step4Title",
    descriptionKey: "credentialScanning.step4Description",
    ctaKey: "credentialScanning.step4Cta",
    ctaPath: "findings",
    icon: <FiSearch size={18} />,
    completedKey: "findings",
    requiresPrevious: "scan",
  },
];

const OnboardingGuide = ({ gitRepoCount, configCount, lastScanAt, onNavigate }) => {
  const { t } = useTranslation();
  const [skippedSteps, setSkippedSteps] = useState({});
  const STEPS = useMemo(() => STEP_DEFS.map((s) => ({
    ...s,
    title: t(s.titleKey),
    description: t(s.descriptionKey),
    cta: t(s.ctaKey),
    badge: s.badgeKind === "required"
      ? t("credentialScanning.badgeRequired")
      : s.badgeKind === "optional"
        ? t("credentialScanning.badgeOptional")
        : undefined,
    skipLabel: s.skipLabelKey ? t(s.skipLabelKey) : undefined,
    completedText: s.completedTextKey ? t(s.completedTextKey) : undefined,
  })), [t]);

  const completed = {
    gitRepo: gitRepoCount > 0,
    config: configCount > 0,
    scan: !!lastScanAt,
    findings: false,
  };

  const isStepAccessible = (step) => {
    if (completed[step.completedKey]) return true;
    if (skippedSteps[step.number]) return false;
    if (step.requiresPrevious) return completed[step.requiresPrevious];
    return true;
  };

  const getStepStatus = (step) => {
    if (completed[step.completedKey]) return "completed";
    if (skippedSteps[step.number]) return "skipped";
    if (!isStepAccessible(step)) return "locked";
    return "active";
  };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 56,
            height: 56,
            borderRadius: 14,
            background: "#eef2ff",
            marginBottom: 16,
          }}
        >
          <FiSearch size={28} style={{ color: "#5177ff" }} />
        </div>
        <h5 style={{ fontWeight: 700, margin: "0 0 6px", fontSize: 20 }}>
          {t("credentialScanning.getStartedTitle")}
        </h5>
        <p style={{ color: "#666", fontSize: 14, margin: 0 }}>
          {t("credentialScanning.getStartedDescription")}
        </p>
      </div>

      {/* Steps */}
      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {STEPS.map((step, idx) => {
          const status = getStepStatus(step);
          const isLast = idx === STEPS.length - 1;

          return (
            <div key={step.number} style={{ display: "flex", gap: 16 }}>
              {/* Left: Circle + Connector line */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: 36,
                  flexShrink: 0,
                }}
              >
                <StepCircle number={step.number} status={status} />
                {!isLast && (
                  <div
                    style={{
                      width: 2,
                      flex: 1,
                      minHeight: 20,
                      background:
                        status === "completed" ? "#43a047" : "#e0e0e0",
                    }}
                  />
                )}
              </div>

              {/* Right: Content */}
              <div
                style={{
                  flex: 1,
                  paddingBottom: isLast ? 0 : 24,
                  opacity: status === "locked" ? 0.45 : 1,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: 15 }}>
                    {step.title}
                  </span>
                  {step.badge && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        padding: "2px 8px",
                        borderRadius: 10,
                        background:
                          step.badgeKind === "required"
                            ? "#fef2f2"
                            : "#f3f4f6",
                        color: step.badgeColor || "#6b7280",
                      }}
                    >
                      {step.badge}
                    </span>
                  )}
                </div>

                <p
                  style={{
                    fontSize: 13,
                    color: "#666",
                    margin: "0 0 12px",
                    lineHeight: 1.5,
                  }}
                >
                  {step.description}
                </p>

                {status === "completed" ? (
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      color: "#43a047",
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    <FiCheck size={14} />
                    {step.completedText}
                  </div>
                ) : status === "skipped" ? (
                  <span style={{ fontSize: 13, color: "#999", fontStyle: "italic" }}>
                    {t("credentialScanning.stepSkipped")}
                  </span>
                ) : status === "active" ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <button
                      onClick={() => onNavigate(step.ctaPath)}
                      style={{
                        padding: "8px 18px",
                        background: "#5177ff",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        fontWeight: 600,
                        cursor: "pointer",
                        fontSize: 13,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        transition: "background 0.15s",
                      }}
                    >
                      {step.icon}
                      {step.cta}
                    </button>
                    {step.skipLabel && (
                      <button
                        onClick={() =>
                          setSkippedSteps((prev) => ({
                            ...prev,
                            [step.number]: true,
                          }))
                        }
                        style={{
                          background: "none",
                          border: "none",
                          color: "#999",
                          fontSize: 13,
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                      >
                        {step.skipLabel}
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    disabled
                    style={{
                      padding: "8px 18px",
                      background: "#e0e0e0",
                      color: "#999",
                      border: "none",
                      borderRadius: 6,
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: "not-allowed",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {step.icon}
                    {step.cta}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StepCircle = ({ number, status }) => {
  if (status === "completed") {
    return (
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: "#43a047",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          flexShrink: 0,
        }}
      >
        <FiCheck size={16} strokeWidth={3} />
      </div>
    );
  }

  if (status === "active") {
    return (
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: "#5177ff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontSize: 14,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {number}
      </div>
    );
  }

  return (
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: "50%",
        border: "2px solid #ddd",
        background: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#bbb",
        fontSize: 14,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {number}
    </div>
  );
};

export default OnboardingGuide;
